import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateLabelMutation } from '@youshift/shared/hooks/mutations/chains';
import { SlotLabel } from '@youshift/shared/types';
import {
  ColorName,
  LabelIcon,
  colorIndices,
  colorNames,
  getRandomColor,
  returnColor,
} from '@youshift/shared/utils';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { YSButton } from '../Buttons';
import IconPicker from '../IconPicker';
import LabelIconComponent from '../LabelIconComponent';
import ColorPicker from '../ColorPicker';
import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';

type SlotLabelsSelectProps = {
  slotLabels: SlotLabel[];
  selectedLabelsId: number[] | null;
  handleSelectedLabelsIdChange: (ids: number | null) => void;
  isCustomLabel: boolean;
  setIsCustomLabel: (value: boolean) => void;
  newLabelName: string;
  setNewLabelName: (value: string) => void;
  labelIcon: LabelIcon | null;
  setLabelIcon: (icon: LabelIcon | null) => void;
  labelColor: ColorName;
  setLabelColor: (color: ColorName) => void;
};

export function SlotLabelsSelect({
  slotLabels,
  selectedLabelsId,
  handleSelectedLabelsIdChange,
  isCustomLabel,
  setIsCustomLabel,
  newLabelName,
  setNewLabelName,
  labelIcon,
  setLabelIcon,
  labelColor,
  setLabelColor,
}: SlotLabelsSelectProps) {
  const queryClient = useQueryClient();
  const { iteration } = useItrContext();
  const { t } = useTranslation();
  const { idItr: id } = useParams();

  const createLabelMutation = useCreateLabelMutation(queryClient, {
    onSuccess: (data, variables) => {
      handleSelectedLabelsIdChange(data.id_slot_label);
      setNewLabelName('');
      setIsCustomLabel(false);
      setLabelIcon(null);
      setLabelColor(getRandomColor());
      setIsCustomLabel(false);
      queryClient.invalidateQueries({
        queryKey: ['slotLabels', Number(iteration.id_chain)],
      });
      // queryClient.setQueryData(
      //   ['slotLabels', Number(iteration.id_chain)],
      //   (oldData: SlotLabel[] | undefined) => {
      //     const newLabel = {
      //       ...data,
      //       name: variables.name,
      //       color: variables.color,
      //       icon: variables.icon,
      //       id_chain: variables.id_chain,
      //     };
      //     if (oldData) {
      //       return [...oldData, newLabel];
      //     }
      //     return [newLabel];
      //   },
      // );
    },
  });

  const isLabelSelected = (labelId: number) =>
    selectedLabelsId?.includes(labelId) || false;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewLabelName(e.target.value);
  };

  const handleSaveNewLabel = () => {
    if (newLabelName.trim()) {
      createLabelMutation.mutate({
        id_itr: id,
        id_chain: iteration.id_chain,
        name: newLabelName,
        color: labelColor,
        icon: labelIcon,
      });
    }
  };

  const handleCancelNewLabel = () => {
    setIsCustomLabel(false);
    setNewLabelName('');
    setLabelIcon(null);
  };

  return (
    <div className="mt-1">
      {isCustomLabel ? (
        <div>
          <div className="flex w-full flex-grow gap-2 items-center">
            <input
              type="text"
              value={newLabelName}
              onChange={handleInputChange}
              placeholder={t('manager.sectionsConfig.enterLabelName')}
              className="w-1/2 p-2 border rounded"
            />
            <IconPicker value={labelIcon} onChange={setLabelIcon} />
            <div className="w-1/3">
              <ColorPicker value={labelColor} onChange={setLabelColor} />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2 mb-2">
            {slotLabels.map(label => (
              <button
                key={label.id_slot_label}
                onClick={() => {
                  handleSelectedLabelsIdChange(label.id_slot_label);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-colors ${
                  isLabelSelected(label.id_slot_label)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: returnColor(label.color, 300),
                  }}
                >
                  <LabelIconComponent icon={label.icon} className="w-4 h-4" />
                </div>
                <span>{label.name}</span>
              </button>
            ))}
          </div>
          {slotLabels.length === 0 && (
            <p className="text-gray-500 text-sm">
              {t('manager.sectionsConfig.noLabelsAvailable')}
            </p>
          )}
        </div>
      )}
      {isCustomLabel ? (
        <div className="flex flex-row gap-2 items-center mt-2">
          {newLabelName.trim() && labelIcon && (
            <button onClick={handleSaveNewLabel} className="text-blue-600">
              {t('generic.save')}
            </button>
          )}
          <button onClick={handleCancelNewLabel} className="text-gray-700">
            {t('generic.cancel')}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsCustomLabel(true)}
          className="text-blue-600 text-sm mt-2"
        >
          {t('manager.sectionsConfig.createLabel')}
        </button>
      )}
    </div>
  );
}
